import { useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';

// Modal "Usar IA para preencher" (título/descrição/descrição SEO Google/palavras-chave). Pedido
// do Jason (28/09/2026). Fluxo: 1) escolhe o tom → 2) gera (Gemini, via painel-node) → 3) revisa/
// edita o resultado → aceita (preenche o formulário, sem salvar sozinho) ou gera de novo. Nunca
// menciona valor do imóvel — regra aplicada no prompt e conferida no servidor; se mesmo assim
// parecer ter passado, um aviso aparece aqui para o corretor revisar antes de usar.
const TONS = [
  { valor: 'objetivo', rotulo: 'Objetivo (fatos, lista de características)' },
  { valor: 'formal', rotulo: 'Formal' },
  { valor: 'informativo', rotulo: 'Informativo' },
  { valor: 'persuasivo', rotulo: 'Persuasivo (foco em vendas)' },
  { valor: 'lazer', rotulo: 'Lazer e descanso' },
  { valor: 'moradia', rotulo: 'Moradia própria (sair do aluguel)' },
];

const LIMITE_TITULO = 50;
const LIMITE_PALAVRAS_CHAVE = 100;
const LIMITE_SEO_DESCRICAO = 158;

export default function GerarAnuncioIa({ show, onHide, dadosImovel, existeConteudo, onAceitar }) {
  const toast = useToast();
  const confirmar = useConfirm();
  const [tom, setTom] = useState('objetivo');
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null); // { titulo, descricao, palavrasChave, possivelValorMencionado }

  function fechar() {
    if (gerando) return; // não fecha no meio de uma geração
    setResultado(null);
    setErro('');
    onHide();
  }

  async function gerar() {
    setGerando(true);
    setErro('');
    try {
      const r = await api.post('/imoveis/ia-anuncio', { ...dadosImovel, tom });
      setResultado(r.data);
    } catch (err) {
      setErro(err.message || 'Não foi possível gerar o texto agora.');
    } finally {
      setGerando(false);
    }
  }

  async function aceitar() {
    if (existeConteudo && !(await confirmar('Isso vai substituir o título, a descrição, a descrição SEO Google e as palavras-chave atuais deste imóvel. Continuar?'))) {
      return;
    }
    onAceitar(resultado);
    toast.sucesso('Texto aplicado — confira e clique em Salvar quando estiver satisfeito.');
    fechar();
  }

  return (
    <Modal show={show} onHide={fechar} size="lg" centered backdrop={gerando ? 'static' : true}>
      <Modal.Header closeButton={!gerando}>
        <Modal.Title className="h6 mb-0">Usar IA para preencher título, descrição e palavras-chave</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Tom do texto</Form.Label>
          <Form.Select value={tom} onChange={(e) => setTom(e.target.value)} disabled={gerando}>
            {TONS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.rotulo}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {erro && <Alert variant="danger">{erro}</Alert>}

        {!resultado && !gerando && (
          <div className="text-muted small mb-2">
            A IA usa os dados já preenchidos no formulário (tipo, quartos, bairro, características etc.) para
            escrever o anúncio. O valor do imóvel NUNCA é mencionado no texto gerado.
          </div>
        )}

        {gerando && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Gerando com IA…
          </div>
        )}

        {resultado && !gerando && (
          <>
            {resultado.possivelValorMencionado && (
              <Alert variant="warning">
                Atenção: o texto gerado pode ter mencionado valor/preço — revise com cuidado antes de usar.
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Título</Form.Label>
              <Form.Control
                maxLength={LIMITE_TITULO}
                value={resultado.titulo}
                onChange={(e) => setResultado((r) => ({ ...r, titulo: e.target.value }))}
              />
              <Form.Text muted>{resultado.titulo.length}/{LIMITE_TITULO}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={resultado.descricao}
                onChange={(e) => setResultado((r) => ({ ...r, descricao: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descrição SEO Google</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={LIMITE_SEO_DESCRICAO}
                value={resultado.seoDescricao}
                onChange={(e) => setResultado((r) => ({ ...r, seoDescricao: e.target.value }))}
              />
              <Form.Text muted>{resultado.seoDescricao.length}/{LIMITE_SEO_DESCRICAO} — é o resumo que aparece no Google</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Palavras-chave</Form.Label>
              <Form.Control
                maxLength={LIMITE_PALAVRAS_CHAVE}
                value={resultado.palavrasChave}
                onChange={(e) => setResultado((r) => ({ ...r, palavrasChave: e.target.value }))}
              />
              <Form.Text muted>{resultado.palavrasChave.length}/{LIMITE_PALAVRAS_CHAVE}</Form.Text>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={fechar} disabled={gerando}>
          Cancelar
        </Button>
        {resultado && !gerando && (
          <Button variant="outline-primary" onClick={gerar}>
            Gerar novamente
          </Button>
        )}
        {!resultado && (
          <Button variant="primary" onClick={gerar} disabled={gerando}>
            {gerando ? 'Gerando…' : 'Gerar com IA'}
          </Button>
        )}
        {resultado && !gerando && (
          <Button className="botao-salvar" onClick={aceitar}>
            Aceitar e usar
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
